import { MongoAbility, RawRuleOf, createMongoAbility, ConditionsMatcher, AbilityBuilder, ForcedSubject } from '@casl/ability';
import { User as UserSharedType } from './types';

export enum Roles {
  MASTER_ADMIN = 'master_admin',
  ORG_ADMIN = 'org_admin',
  BASIC_USER = 'basic_user',
}

export type AppAbility = MongoAbility<[Actions, Resources | ForcedSubject<Resources>], Conditions>;
export type AppRule = RawRuleOf<AppAbility>;

export enum Actions {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  MANAGE = 'manage',
}

export enum Resources {
  ALL = 'all',
  ORGANIZATION = 'Organization',
  BILLING = 'Billing',
  DIRECTORY = 'Directory',
  PATIENT = 'Patient',
}

export type Conditions = {
  [key: string]: any;
};

export const createAppAbility = (rules: AppRule[] = []) => {
  const conditionsMatcher: ConditionsMatcher<any> = (conditions: Conditions) => (subject: Record<string, unknown>) => {
    if (!conditions || !subject) return false;

    const checkConditions = (conditionObj: any, subjectObj: any): boolean => {
      for (const [key, value] of Object.entries(conditionObj)) {
        const subjectValue = subjectObj && subjectObj[key] !== undefined ? subjectObj[key] : undefined;
        if (subjectValue === undefined) return false;

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          if (!checkConditions(value, subjectValue)) return false;
          continue;
        }

        if (Array.isArray(value)) {
          if (Array.isArray(subjectValue)) {
            if (!subjectValue.some(val => value.includes(val))) return false;
            continue;
          }
          if (!value.includes(subjectValue)) return false;
          continue;
        }

        if (subjectValue !== value) return false;
      }
      return true;
    };

    return checkConditions(conditions, subject);
  };

  return createMongoAbility<AppAbility>(rules, { conditionsMatcher });
};

export const abilityDefiner = (orgs: { org_id: number, user_role: string }[], user: UserSharedType | null) => {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!user) {
    return build();
  }

  if (user.master_admin) {
    can(Actions.MANAGE, Resources.ALL);
    return build();
  }

  const readOrgList: number[] = []
  const manageOrgList: number[] = []

  for (const org of orgs) {
    switch (org.user_role) {
      case Roles.ORG_ADMIN:
        manageOrgList.push(org.org_id);
        break;
      case Roles.BASIC_USER:
        readOrgList.push(org.org_id);
        break;
      default:
        break;
    }
  }

  if (manageOrgList.length > 0) {
    can(Actions.MANAGE, Resources.ORGANIZATION, { organization_id: manageOrgList });
    can(Actions.MANAGE, Resources.BILLING, { organization_id: manageOrgList });
    can(Actions.READ, Resources.PATIENT, { organization_id: manageOrgList });
  }

  if (readOrgList.length > 0) {
    can(Actions.READ, Resources.ORGANIZATION, { organization_id: readOrgList });
    can(Actions.READ, Resources.PATIENT, { organization_id: readOrgList });
  }

  can(Actions.READ, Resources.DIRECTORY);

  return build();
};
